import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { VideoEmbed } from '../components/VideoEmbed';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Search, MessageSquare, Eye, Clock, Loader2, Send } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DiscussionsPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [discussions, setDiscussions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [replies, setReplies] = useState([]);
  const [newReply, setNewReply] = useState('');
  const [newDiscussion, setNewDiscussion] = useState({
    title: '',
    content: '',
    category: 'general',
  });

  const fetchDiscussions = useCallback(async () => {
    try {
      const categoryParam = selectedCategory !== 'all' ? `?category=${selectedCategory}` : '';
      const [discRes, catRes] = await Promise.all([
        axios.get(`${API}/discussions/${categoryParam}`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/discussions/categories`, { headers: getAuthHeaders(), withCredentials: true }),
      ]);
      setDiscussions(discRes.data);
      setCategories(catRes.data);
    } catch (error) {
      toast.error('Failed to load discussions');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, selectedCategory]);

  useEffect(() => {
    fetchDiscussions();
  }, [fetchDiscussions]);

  const handleCreateDiscussion = async () => {
    if (!newDiscussion.title.trim() || !newDiscussion.content.trim()) return;
    setCreating(true);

    try {
      await axios.post(`${API}/discussions/`, newDiscussion, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      toast.success('Discussion created!');
      setShowCreateDialog(false);
      setNewDiscussion({ title: '', content: '', category: 'general' });
      fetchDiscussions();
    } catch (error) {
      toast.error('Failed to create discussion');
    } finally {
      setCreating(false);
    }
  };

  const openDiscussion = async (discussion) => {
    setSelectedDiscussion(discussion);
    try {
      const response = await axios.get(`${API}/discussions/${discussion.discussion_id}/replies`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setReplies(response.data);
    } catch (error) {
      toast.error('Failed to load replies');
    }
  };

  const handleReply = async () => {
    if (!newReply.trim() || !selectedDiscussion) return;

    try {
      await axios.post(
        `${API}/discussions/${selectedDiscussion.discussion_id}/reply`,
        { content: newReply },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setNewReply('');
      const response = await axios.get(`${API}/discussions/${selectedDiscussion.discussion_id}/replies`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setReplies(response.data);
      fetchDiscussions();
    } catch (error) {
      toast.error('Failed to add reply');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / 86400000);
    if (days < 1) return 'Today';
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const filteredDiscussions = discussions.filter(
    (d) =>
      d.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background page-with-bottom-nav" data-testid="discussions-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 pt-24 pb-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold">Discussion Boards</h1>
            <p className="text-muted-foreground mt-1">
              Open discussions about immigration topics
            </p>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-discussion-btn">
                <Plus className="h-4 w-4 mr-2" />
                Start Discussion
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">Start New Discussion</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="disc-title">Title</Label>
                  <Input
                    id="disc-title"
                    placeholder="What do you want to discuss?"
                    value={newDiscussion.title}
                    onChange={(e) => setNewDiscussion((prev) => ({ ...prev, title: e.target.value }))}
                    className="input-default"
                    data-testid="discussion-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="disc-category">Category</Label>
                  <Select
                    value={newDiscussion.category}
                    onValueChange={(val) => setNewDiscussion((prev) => ({ ...prev, category: val }))}
                  >
                    <SelectTrigger className="input-default">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="disc-content">Content</Label>
                  <Textarea
                    id="disc-content"
                    placeholder="Share your thoughts..."
                    value={newDiscussion.content}
                    onChange={(e) => setNewDiscussion((prev) => ({ ...prev, content: e.target.value }))}
                    className="input-default min-h-[150px]"
                    data-testid="discussion-content-input"
                  />
                </div>
                <Button
                  onClick={handleCreateDiscussion}
                  disabled={creating || !newDiscussion.title.trim() || !newDiscussion.content.trim()}
                  className="w-full btn-primary"
                  data-testid="submit-discussion-btn"
                >
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Post Discussion'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search discussions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 input-default"
              data-testid="search-discussions-input"
            />
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full sm:w-[200px] input-default">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Discussions List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredDiscussions.length === 0 ? (
          <Card className="card-default text-center py-12">
            <p className="text-muted-foreground">No discussions found. Start one!</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredDiscussions.map((discussion, index) => (
              <Card
                key={discussion.discussion_id}
                className="card-default hover:card-active cursor-pointer transition-all animate-slide-up"
                style={{ animationDelay: `${index * 0.03}s` }}
                onClick={() => openDiscussion(discussion)}
                data-testid={`discussion-${discussion.discussion_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex gap-4">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={discussion.author_picture} />
                      <AvatarFallback>{discussion.author_name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-semibold text-lg truncate">{discussion.title}</h3>
                        <Badge variant="secondary" className="shrink-0">
                          {categories.find((c) => c.id === discussion.category)?.name || discussion.category}
                        </Badge>
                      </div>
                      <p className="text-muted-foreground text-sm mt-1 line-clamp-2">{discussion.content}</p>
                      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        <span>{discussion.author_name}</span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(discussion.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="h-3 w-3" />
                          {discussion.reply_count} replies
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="h-3 w-3" />
                          {discussion.view_count} views
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Discussion Detail Dialog */}
        <Dialog open={!!selectedDiscussion} onOpenChange={() => setSelectedDiscussion(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            {selectedDiscussion && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={selectedDiscussion.author_picture} />
                      <AvatarFallback>{selectedDiscussion.author_name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">{selectedDiscussion.author_name}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(selectedDiscussion.created_at)}</p>
                    </div>
                  </div>
                  <DialogTitle className="font-serif text-xl">{selectedDiscussion.title}</DialogTitle>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto space-y-4 py-4">
                  <DiscussionContent text={selectedDiscussion.content} />
                  
                  <div className="border-t border-border pt-4">
                    <h4 className="font-semibold mb-4">Replies ({replies.length})</h4>
                    <div className="space-y-4">
                      {replies.map((reply) => (
                        <div key={reply.reply_id} className="flex gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={reply.author_picture} />
                            <AvatarFallback>{reply.author_name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 bg-muted/50 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-sm font-medium">{reply.author_name}</p>
                              <p className="text-xs text-muted-foreground">{formatDate(reply.created_at)}</p>
                            </div>
                            <DiscussionContent text={reply.content} className="text-sm" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="border-t border-border pt-4 flex gap-2">
                  <Textarea
                    placeholder="Write a reply..."
                    value={newReply}
                    onChange={(e) => setNewReply(e.target.value)}
                    className="input-default min-h-[60px]"
                    data-testid="reply-input"
                  />
                  <Button
                    onClick={handleReply}
                    disabled={!newReply.trim()}
                    className="btn-primary"
                    data-testid="submit-reply-btn"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

// Renders text with auto-embedded YouTube videos (one embed per URL)
const DiscussionContent = ({ text, className }) => {
  if (!text) return null;
  const ytRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/g;
  const parts = [];
  let lastIdx = 0;
  let match;
  const seenIds = new Set();
  
  while ((match = ytRegex.exec(text)) !== null) {
    const ytId = match[1];
    if (seenIds.has(ytId)) continue;
    seenIds.add(ytId);
    if (match.index > lastIdx) {
      parts.push(<p key={`t-${lastIdx}`} className={`text-muted-foreground whitespace-pre-wrap ${className || ''}`}>{text.slice(lastIdx, match.index)}</p>);
    }
    parts.push(
      <div key={`yt-${ytId}`} className="my-2 rounded-lg overflow-hidden aspect-video">
        <iframe src={`https://www.youtube.com/embed/${ytId}`} className="w-full h-full" allowFullScreen title="YouTube video" />
      </div>
    );
    lastIdx = match.index + match[0].length;
  }
  
  if (lastIdx < text.length) {
    parts.push(<p key={`t-${lastIdx}`} className={`text-muted-foreground whitespace-pre-wrap ${className || ''}`}>{text.slice(lastIdx)}</p>);
  }
  
  return parts.length > 0 ? <div className="space-y-2">{parts}</div> : <p className={`text-muted-foreground whitespace-pre-wrap ${className || ''}`}>{text}</p>;
};

export default DiscussionsPage;
